import { memo, useCallback, useState } from 'react'

import { AddCircle, Refresh } from '@mui/icons-material'
import { Box, Button, SxProps, Typography, Theme, colors, Tooltip } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { Experience } from 'domains/Workspaces/Events/Event/Watch'
import { NewAdPodDialogContent, AdPods } from 'domains/Workspaces/Events/Event/Watch/AdPods'
import { eventWatchRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'
import { ThemeTypes } from 'stores/settings'

const MAX_ADS_NUMBER = 100

const variant = (mode?: Theme['palette']['mode']) => (mode === ThemeTypes.DARK ? 700 : 300)

const gridStyles: SxProps<Theme> = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: '1fr 340px',
  gridAutoRows: 'auto',
}

const WatchRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const [dialog, setDialog] = useState(false)

  const queryClient = useQueryClient()

  const { eventId } = eventWatchRoute.useParams()

  const eventQuery = useQuery(eventQueryKeys.detail({ eventId }))
  const { data: event } = eventQuery

  const adPodsQuery = useQuery(eventQueryKeys.adPods({ eventId }))
  const { data: ads } = adPodsQuery

  const getAdsCreatedColor = useCallback(
    (mode?: Theme['palette']['mode']) => {
      if (!ads) return mode === 'dark' ? colors.common.white : colors.common.black

      if (ads.length >= MAX_ADS_NUMBER) {
        return colors.red[variant(mode)]
      } else if (ads.length >= 85) {
        return colors.deepOrange[variant(mode)]
      } else if (ads.length > 70) {
        return colors.yellow[variant(mode)]
      } else {
        return mode === 'dark' ? colors.common.white : colors.common.black
      }
    },
    [ads],
  )

  const handleRefresh = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: eventQueryKeys.detail({ eventId }).queryKey }),
      queryClient.invalidateQueries({ queryKey: eventQueryKeys.adPods({ eventId }).queryKey }),
    ])
  }, [eventId, queryClient])

  if (eventQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!event) {
    return <NotFound />
  }

  return (
    <>
      <Box sx={{ ...gridStyles, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Tooltip title={t('refresh')} placement="left">
            <AnimatedCounterButton sx={{ mb: -1, ml: 'auto' }} action={handleRefresh}>
              {({ key, ...props }) => <Refresh key={key} {...props} />}
            </AnimatedCounterButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexShrink: 1 }}>
          <Typography variant="heading-md">{t('domain:Workspace.Event.adSchedule')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {ads?.length ? (
              <Tooltip arrow placement="left" title={t('domain:Workspace.Event.adsTooltip')}>
                <Typography variant="text-sm" sx={{ color: (theme) => getAdsCreatedColor(theme.palette.mode) }}>
                  {t('domain:Workspace.Event.adsCreated', { count: ads.length })}
                </Typography>
              </Tooltip>
            ) : null}
            <Button
              disabled={ads && ads.length >= MAX_ADS_NUMBER}
              variant="contained"
              onClick={() => setDialog(true)}
              endIcon={<AddCircle />}
            >
              {t('domain:Workspace.Event.adCreateButton')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 52.5px (height of the above grid row) */}
      <Box sx={{ ...gridStyles, height: 'calc(100% - 52.5px)' }}>
        <Experience />
        <AdPods event={event} ads={ads} isLoading={adPodsQuery.isLoading} />
      </Box>

      <Dialog open={dialog} onClose={() => setDialog(false)} fullWidth>
        <NewAdPodDialogContent eventId={eventId} />
      </Dialog>
    </>
  )
}

export const Watch = memo(WatchRaw)
